import React, { useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import * as authService from '../../services/authService';
import '../start/PasswordGate.css';
import './LoginPage.css';

const focusRowInput = (e) => {
  const row = e.currentTarget;
  const input = row && row.querySelector('input');
  if (input && !input.disabled) {
    input.focus();
  }
};

const EyeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M2 12C2 12 5.5 5 12 5C18.5 5 22 12 22 12C22 12 18.5 19 12 19C5.5 19 2 12 2 12Z"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.75" />
  </svg>
);

const EyeOffIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M17.94 17.94C16.22 19.26 14.17 20 12 20C5.5 20 2 13 2 13C3.07 10.94 4.76 9.28 6.76 8.18M9.9 6.64C10.58 6.46 11.28 6.36 12 6.36C18.5 6.36 22 13 22 13C21.27 14.55 20.18 15.92 18.88 17.02M14.12 14.12C13.79 14.45 13.41 14.71 12.99 14.88"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M2 2L22 22" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
  </svg>
);

/**
 * Reset Password ΓÇö opened from Forgot Password email / dev link.
 * Route: #/reset-password?token=...
 */
const ResetPasswordPage = () => {
  const history = useHistory();
  const location = useLocation();
  const token = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    return params.get('token') || '';
  }, [location.search]);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');

    if (!token) {
      setError('Invalid or missing reset link. Please request a new one.');
      return;
    }
    if (!password) {
      setError('Please enter a new password.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (!confirmPassword) {
      setError('Please confirm your new password.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setBusy(true);
    try {
      const data = await authService.resetPassword({ token, password });
      setInfo(data.message || 'Password updated. Please sign in.');
      setTimeout(() => {
        history.replace('/login');
      }, 1200);
    } catch (err) {
      setError(err.message || 'Could not reset password. Link may be expired.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="xk-gate">
      <div className="xk-gate__card xk-login-card">
        <img
          src={`${process.env.PUBLIC_URL || ''}/XLog3D Logo_Red.png`}
          alt="XLog3D"
          className="xk-gate__logo"
        />

        <div className="xk-gate__note">
          <div className="xk-gate__note-title">Attention Please!</div>
          <p>Please use computer to run the App</p>
        </div>

        <h2 className="xk-gate__title">Reset Password</h2>

        {!token ? (
          <>
            <div className="xk-gate__error">
              Invalid reset link. Use Forgot password on the Login page to get a new one.
            </div>
            <div className="xk-login-links">
              <button
                type="button"
                className="xk-login-link"
                onClick={() => history.push('/login?mode=forgot')}
              >
                Forgot password
              </button>
              <button
                type="button"
                className="xk-login-link"
                onClick={() => history.push('/login')}
              >
                Back to Login
              </button>
            </div>
          </>
        ) : (
          <form className="xk-gate__form" onSubmit={handleSubmit} autoComplete="off" noValidate>
            <div className="xk-gate__row xk-login-password-row" onMouseDown={focusRowInput}>
              <label className="xk-gate__row-label" htmlFor="reset-password">
                New Password
              </label>
              <div className="xk-login-password-field">
                <input
                  id="reset-password"
                  className="xk-gate__row-input"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  disabled={busy}
                  autoFocus
                  autoComplete="new-password"
                  placeholder="At least 8 characters"
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (error) setError('');
                  }}
                />
                <button
                  type="button"
                  className="xk-login-password-toggle"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  disabled={busy}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setShowPassword((prev) => !prev)}
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            </div>

            <div className="xk-gate__row xk-login-password-row" onMouseDown={focusRowInput}>
              <label className="xk-gate__row-label" htmlFor="reset-confirm">
                Confirm Password
              </label>
              <div className="xk-login-password-field">
                <input
                  id="reset-confirm"
                  className="xk-gate__row-input"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  disabled={busy}
                  autoComplete="new-password"
                  placeholder="Re-enter new password"
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (error) setError('');
                  }}
                />
              </div>
            </div>

            {error && <div className="xk-gate__error">{error}</div>}
            {info && <div className="xk-login-info">{info}</div>}

            <button type="submit" className="xk-gate__submit" disabled={busy}>
              {busy ? 'Please wait...' : 'Reset Password'}
            </button>
          </form>
        )}

        {token && (
          <div className="xk-login-links">
            <button
              type="button"
              className="xk-login-link"
              disabled={busy}
              onClick={() => history.push('/login')}
            >
              Back to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
